import { useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2 } from 'lucide-react';

const DeleteConfirmationModal = ({ 
  isOpen, 
  onConfirm, 
  onCancel,
  isDeleting = false,
  postTitle = '',
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isDeleting) {
        onCancel();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isDeleting, onCancel]);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm touch-none"
            onClick={!isDeleting ? onCancel : undefined}
            onWheel={(e) => e.preventDefault()}
            onTouchMove={(e) => e.preventDefault()}
          />

          {/* Modal */}
          <div 
            className="fixed inset-0 z-[111] flex items-center justify-center px-6"
            onClick={!isDeleting ? onCancel : undefined}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="bg-white w-full max-w-sm rounded-2xl overflow-hidden shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="delete-modal-title"
              aria-describedby="delete-modal-description"
            >
              {/* Content */}
              <div className="p-6 text-center space-y-3">
                {/* Icon */}
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                  <Trash2 className="w-7 h-7 text-red-500" />
                </div>
                
                {/* Title */}
                <h3 
                  id="delete-modal-title"
                  className="text-lg font-bold text-foreground"
                >
                  {t('delete_post_question', 'Delete Post?')}
                </h3>
                
                {/* Description */}
                <p 
                  id="delete-modal-description"
                  className="text-sm text-muted-foreground leading-relaxed"
                >
                  {t(
                    'delete_post_warning', 
                    'This action cannot be undone. Your post will be permanently removed from the community feed.'
                  )}
                </p>

                {/* Post title preview if available */}
                {postTitle && (
                  <div className="mt-3 px-3 py-2 bg-gray-50 rounded-lg">
                    <p className="text-xs text-muted-foreground truncate">
                      "{postTitle}"
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex border-t border-border">
                <button
                  onClick={onCancel}
                  disabled={isDeleting}
                  className="flex-1 py-4 text-sm font-semibold text-foreground 
                             hover:bg-gray-50 transition-colors border-r border-border
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('cancel', 'Cancel')}
                </button>
                <button
                  onClick={onConfirm}
                  disabled={isDeleting}
                  className="flex-1 py-4 text-sm font-semibold text-red-600 
                             hover:bg-red-50 transition-colors
                             disabled:opacity-50 disabled:cursor-not-allowed
                             flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
                      {t('deleting', 'Deleting...')}
                    </>
                  ) : (
                    t('delete', 'Delete')
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default memo(DeleteConfirmationModal);

